import { AuditDetails, Entity, EntityLink } from '../../entity';
import { ScheduleRule } from '../../../scheduling/schedule.rule';
import { ActivityEntityName } from '../../shared/activity.entity.name';
import { TaskEvidenceOptions } from '../../tasks/task.evidence';
import { ScheduleClass, ScheduleContext, ScheduleExtraDate, ScheduleSkip } from '../../../scheduling/schedule.types';
import { ServicePlanBlackoutRange } from '../service.plan/service.plan.blackout.range';
import { PlannedTaskReportRule } from './planned.task.report.rule';

/**
 * Attached = inherited from the plan's standing record; Local = brand-new work that exists only on
 * this Visit. There is no third state: a Visit may cancel a planned record or add a new one, and
 * editing a planned record's content on a Visit is not a mutation the product offers.
 */
export enum PlannedWorkAttachmentKind { Attached = 'Attached', Local = 'Local' }
export interface PlannedWorkAttached { kind: PlannedWorkAttachmentKind.Attached; activeSourceId: string; }
export interface PlannedWorkLocal { kind: PlannedWorkAttachmentKind.Local; }
export type PlannedWorkAttachment = PlannedWorkAttached | PlannedWorkLocal;
export interface PlannedTaskConstructionFields {
    attachment: PlannedWorkAttachment;
    parentOccurrenceNodeId?: string;
}

/**
 * Task-level schedule exceptions. Plan and line blackouts are masks owned by the plan and the line,
 * and are deliberately not here: those describe when the CONTRACT is dark, this describes what one
 * task's own cadence skips or adds.
 */
export interface PlannedTaskScheduleExceptions {
    /** Skipped dates and inclusive blackout ranges authored on this task (EXDATE). */
    skips?: ScheduleSkip[];
    /** One-off extra dates authored on this task (RDATE). */
    extras?: ScheduleExtraDate[];
}

/** Everything the schedule editor needs to preview a cadence: the resolved context and both blackout masks. */
export interface PlannedTaskAuthoringContext {
    context: ScheduleContext;
    lineBlackouts?: ServicePlanBlackoutRange[];
    planBlackouts?: ServicePlanBlackoutRange[];
}

/**
 * How one Visit differs from the recurring plan for one task.
 *
 * Two states, because a Visit can do exactly two things: cancel planned work, or add work of its own.
 * There is no Modified — a planned record's content is edited on the plan, where the change applies to
 * every future Visit, so a per-attendance content edit has no representation and no badge.
 */
export enum VisitTaskChange {
    Excluded = 'Excluded',
    Added = 'Added for this visit',
}

/**
 * Phase two of the planned-work storage model: one `planned_tasks` collection for live tasks, template
 * tasks and Visit exceptions, mirroring `planned_items`.
 *
 * A standing task belongs to a line (root task) or to another standing task (subtask); a template
 * task belongs to a template line or template task. A Visit record has `scope: 'visit'`, a `visitId`,
 * and explicit attachment: Attached names the standing record it cancels, Local is a task added on this
 * Visit alone. Visit rows carry CONTENT only: schedule, revisions and horizons are standing-task
 * concerns and never appear on one.
 *
 * Unlike items, tasks ship WITH a tenant upgrade: existing live and template tasks, their schedule
 * state and their Visit task overrides are lifted into this collection. Live and template rows keep
 * their EXISTING `_id` — the schedule side tables (`due_days`, `schedule_ids`, horizon cursors) name
 * tasks by that id and by `scheduleId`, so re-minting would orphan every projected occurrence.
 * Lifted overrides take a deterministic id derived from the override they came from, which is what
 * makes rerunning the upgrade converge rather than duplicate.
 */
export enum PlannedTaskScope {
    Standing = 'standing',
    Visit = 'visit',
    NextN = 'nextN',
    DateRange = 'dateRange',
}

export type PlannedTaskParentType = ActivityEntityName.ServiceLine | ActivityEntityName.ServiceLineTask | ActivityEntityName.ServiceLineTemplate | ActivityEntityName.ServiceLineTaskTemplate;

export interface PlannedTaskParent {
    type: PlannedTaskParentType;
    _id: string;
}

export interface PlannedTaskRange {
    from: Date;
    to: Date;
}

/**
 * How a task behaves when Job construction groups several Visits into one Job. Consumed only by
 * construction: approval and rescheduling never merge Visit records.
 */
export enum PlannedTaskConstructionRule {
    KeepOne = 'KeepOne',
    KeepEachOccurrence = 'KeepEachOccurrence',
}

// Absent means KeepOne, so existing rows need no backfill and fingerprints stay stable.
export const DEFAULT_TASK_CONSTRUCTION_RULE = PlannedTaskConstructionRule.KeepOne;

/** Planner wording for each rule, worded for Job construction; the member names are storage vocabulary. */
export const PLANNED_TASK_CONSTRUCTION_RULE_LABELS: Readonly<Record<PlannedTaskConstructionRule, string>> = {
    [PlannedTaskConstructionRule.KeepOne]: 'Keep one task when Visits are combined',
    [PlannedTaskConstructionRule.KeepEachOccurrence]: 'Keep a task for each Visit',
};

/** The authored task content a row carries. Scheduling remains a plan decision. */
export interface PlannedTaskContent extends TaskEvidenceOptions {
    name: string;
    description?: string;
    workCategory?: Entity;
    defaultResources?: Entity[];
    mandatory?: boolean;
    mandatoryForClaiming?: boolean;
    duration?: number;
    color?: string;
    /** Plan and template records only; a Visit write carrying one is refused. */
    constructionRule?: PlannedTaskConstructionRule;
    /**
     * A legal deadline: passing its due date without the work done raises a compliance flag. Explicit,
     * because a cadence alone ("clean filters 3 months after service") is not a legal obligation.
     * Plan and template records only; absent means not statutory.
     */
    statutory?: boolean;
    /**
     * How Service history judges this task's completions (plan section 19). Root tasks on plan and
     * template records only; absent shows completions without judging them. Reporting only, so the
     * approval fingerprint ignores it: editing a rule never asks for re-approval.
     */
    reportRule?: PlannedTaskReportRule | null;
}

/**
 * Every authored content field, named once so the copy paths (resolution, template capture,
 * template-to-live) cannot each keep a private list that silently drops a newly added field.
 * `disabled` is absent: it is a standing-only scheduling decision, not content a Visit row shows.
 */
export const PLANNED_TASK_CONTENT_FIELDS = ['name', 'description', 'workCategory', 'defaultResources', 'mandatory', 'mandatoryForClaiming', 'duration', 'color', 'signatureEnabled', 'commentEnabled', 'beforePhotoEnabled', 'duringPhotoEnabled', 'afterPhotoEnabled', 'fileEnabled', 'fileTemplate', 'formEnabled', 'formTemplate', 'evidenceEnforcement', 'constructionRule', 'statutory', 'reportRule'] as const satisfies readonly (keyof PlannedTaskContent)[];

/** The authored content of any task-shaped row, leaving absent fields absent. */
export function pickPlannedTaskContent(task: PlannedTaskContent): PlannedTaskContent {

    const content: PlannedTaskContent = { name: task.name };
    for (const field of PLANNED_TASK_CONTENT_FIELDS) {
        if (task[field] !== undefined) Object.assign(content, { [field]: task[field] });
    }

    return content;
}

export function isTaskConstructionRule(value: unknown): value is PlannedTaskConstructionRule {

    return Object.values(PlannedTaskConstructionRule).includes(value as PlannedTaskConstructionRule);
}

export function effectiveTaskConstructionRule(task: Pick<PlannedTaskContent, 'constructionRule'> | null | undefined): PlannedTaskConstructionRule {

    const rule = task?.constructionRule;

    return isTaskConstructionRule(rule) ? rule : DEFAULT_TASK_CONSTRUCTION_RULE;
}

/** Only an explicit `true` is statutory; false and absent are the same answer and hash the same. */
export function isStatutoryTask(task: Pick<PlannedTaskContent, 'statutory'> | null | undefined): boolean {

    return task?.statutory === true;
}

/** A subtask cannot be kept once while its root repeats per Visit, so a KeepEachOccurrence root wins. */
export function effectiveSubtaskRule(root: Pick<PlannedTaskContent, 'constructionRule'> | null | undefined, child: Pick<PlannedTaskContent, 'constructionRule'> | null | undefined): PlannedTaskConstructionRule {

    return effectiveTaskConstructionRule(root) === PlannedTaskConstructionRule.KeepEachOccurrence
        ? PlannedTaskConstructionRule.KeepEachOccurrence
        : effectiveTaskConstructionRule(child);
}

// `Entity.name` is optional; a task always has one, so the content contract owns `name`.
export interface PlannedTask extends Omit<Entity, 'name'>, AuditDetails, PlannedTaskContent {
    /** Live plan owner. Absent for template parents. */
    servicePlanId?: string;
    /** Template owner. Absent for live parents. */
    servicePlanTemplateId?: string;
    /**
     * The denormalised owner chain a ROOT task is stamped with: its Site, its Service Plan, its
     * Service Line. The platform's ordinary `links` array, exactly as Job, Task, Invoice, Purchase
     * Order, Stock Location and Equipment Link carry one, and served by a multikey index of the same
     * family — here `{ 'links.type': 1, 'links._id': 1, scheduleClass: 1, active: 1, isSubtask: 1 }`,
     * because this collection is asked "which owner" as often as "which id".
     *
     * The board reads this, not the plan. A due-day row is keyed `(date, siteId, bucket)`, so a
     * PATTERN task already arrives from the bitmap carrying the site it is due at. A CLASS task —
     * daily, weekdays, or the working-day default — is deliberately absent from the bitmap, because a
     * row per task per working day is the write volume the class shortcut exists to avoid. Without
     * this stamp the class half of Upcoming Work has to reach task → plan → site to learn where it
     * happens, which is two joins after the sort has already been paid for, and turns the cheap half
     * of the board into the expensive one.
     *
     * Ids are UUIDs, so a link `_id` is unambiguous on its own — a match on the id alone can never
     * collide with a different owner's. The `type` is what lets a reader say WHICH owner a link is
     * without another read, and what lets the index seek straight to one kind of owner rather than
     * every link on every task. A query that binds BOTH must say so with `$elemMatch: { type, _id }`:
     * two dotted predicates would be satisfied by two different elements of the array.
     *
     * ADDITIVE, never a replacement. `servicePlanId` and `parent` stay exactly as they are — four
     * indexes and most of the read surface are built on them, and a denormalisation that quietly
     * became the primary pointer would be a migration wearing a cache's clothes.
     *
     * Root tasks only: a subtask inherits its root's owners, and a second copy is a second thing to
     * keep true. The plan and line links are IMMUTABLE once stamped — a Service Line cannot move
     * between plans and a task cannot move between lines — so only the Site link is ever rewritten,
     * by the plan-site-change reaction. A plan with no site is stamped with the other two and no Site
     * link at all: the due-day bitmap's `DUE_DAY_NO_SITE` sentinel is a KEY concern, and a link
     * claiming a site named `__nosite__` would be asserting something nobody has.
     *
     * Existing root tasks were stamped when this owner chain was introduced.
     */
    links?: EntityLink[];
    parent: PlannedTaskParent;
    scope: PlannedTaskScope;
    /** LexoRank position within the parent. */
    rank: string;
    /** True when `parent.type` is a task; kept as a field so the tree reads it without a lookup. */
    isSubtask: boolean;
    /** Standing tasks only: a disabled task is kept but never scheduled. Not a Visit decision. */
    disabled?: boolean;

    /** Standing tasks only: cadence. */
    schedule?: ScheduleRule;
    scheduleExceptions?: PlannedTaskScheduleExceptions;
    startsOn?: Date | null;
    scheduleId?: number;
    scheduleClass?: ScheduleClass;
    scheduleRevision?: number;
    lastProjectedRevision?: number;
    projectedUntil?: string;
    nextDueAfterHorizon?: string;
    lastCompleted?: string;
    integrationId?: string;
    integrationCallerId?: string;

    /** Visit exceptions only. */
    visitId?: string;
    range?: PlannedTaskRange;
    remaining?: number;
    /** Standing definitions use their own _id; a Visit row declares whether it cancels planned work or adds its own. */
    attachment?: PlannedWorkAttachment;
    parentOccurrenceNodeId?: string;
    excluded?: boolean;
}

export interface PlannedTaskOrigin {
    type: PlannedTaskParentType;
    _id: string;
    name: string;
}

/** A resolved task for a standing outline or Visit tree. Cadence is present only on standing rows.
 * Pick keeps the four cadence fields aligned with their persisted PlannedTask types.
 */
export interface PlannedTaskLine extends Omit<Entity, 'name'>, PlannedTaskContent, Pick<PlannedTask, 'schedule' | 'scheduleExceptions' | 'startsOn' | 'lastCompleted'> {
    attachment: PlannedWorkAttachment;
    parentOccurrenceNodeId?: string;
    parent: PlannedTaskParent;
    origin: PlannedTaskOrigin;
    scope: PlannedTaskScope;
    rank: string;
    isSubtask: boolean;
    visitId?: string;
    exceptionId?: string;
    visitChange?: VisitTaskChange;
    addedForVisit?: boolean;
    readonly?: boolean;
}

export enum PlannedTaskCommandId {
    Exclude = 'PlannedTaskExcludeCommand',
    Restore = 'PlannedTaskRestoreCommand',
    Remove = 'PlannedTaskRemoveCommand',
    Move = 'PlannedTaskMoveCommand',
    BulkCreate = 'PlannedTaskBulkCreateCommand',
}

/** Entity-data payload for creating one ordered block of standing tasks under a validated parent. */
export interface PlannedTaskBulkCreateRequest {
    parent: PlannedTaskParent;
    items: Partial<PlannedTask>[];
}

/**
 * A Visit row's editor identity: the Visit plus the standing task it stands for.
 *
 * Same reasoning as `buildPlannedItemLineId` and the older `buildVisitTaskId` it supersedes. A Visit
 * board row addressed by the standing task's own id would open the recurring task, and a save there
 * would write the plan — the exact mutation a Visit exception exists to prevent. Scoping the id at
 * the boundary makes that write unrepresentable rather than merely discouraged.
 *
 * Standing-only boards (a Service Line's Tasks tab) address rows by the record id directly.
 */
const PLANNED_TASK_ID_PREFIX = 'planned-task.';

export function buildPlannedTaskLineId(visitId: string, taskId: string): string {

    return `${PLANNED_TASK_ID_PREFIX}${encodeURIComponent(JSON.stringify([visitId, taskId]))}`;
}

export function readPlannedTaskLineId(id: string): [string, string] | null {

    if (!id?.startsWith(PLANNED_TASK_ID_PREFIX)) return null;
    try {
        const parts: unknown = JSON.parse(decodeURIComponent(id.slice(PLANNED_TASK_ID_PREFIX.length)));
        return Array.isArray(parts) && parts.length === 2 && parts.every(part => typeof part === 'string' && !!part) ? parts as [string, string] : null;
    } catch { return null; }
}

export const PLANNED_TASK_PARENT_TYPES: readonly PlannedTaskParentType[] = [ActivityEntityName.ServiceLine, ActivityEntityName.ServiceLineTask, ActivityEntityName.ServiceLineTemplate, ActivityEntityName.ServiceLineTaskTemplate];

export function isPlannedTaskParentType(value: unknown): value is PlannedTaskParentType {

    return PLANNED_TASK_PARENT_TYPES.includes(value as PlannedTaskParentType);
}

/** A template parent — the half of the collection that must never carry schedule or projection state. */
export function isTemplateTaskParent(parent: PlannedTaskParent): boolean {

    return parent?.type === ActivityEntityName.ServiceLineTemplate || parent?.type === ActivityEntityName.ServiceLineTaskTemplate;
}

/** A task parent rather than a line parent — the storage fact behind `isSubtask`. */
export function isPlannedTaskSubtaskParent(parent: PlannedTaskParent): boolean {

    return parent?.type === ActivityEntityName.ServiceLineTask || parent?.type === ActivityEntityName.ServiceLineTaskTemplate;
}

/** The collection this phase introduces; pinned here so schema, repository and upgrade cannot drift. */
export const PlannedTaskCollectionName = 'planned_tasks';

/**
 * The row-id prefixes the ONE planned-task outline keys its two levels by.
 *
 * There used to be three pairs — one per projection — because there used to be three projections. A
 * live plan emitted `service-line:` / `planned-task:`, a template emitted its own, a Visit a third.
 * One projection now serves all three parents, so it emits ONE pair, and the three sets of entity
 * kinds the web draws them with have to agree with it: the relation builder decides whether a focused
 * row is a task by testing `rowId.startsWith(kinds.taskPrefix)`, and a client pair that disagreed with
 * the server's made that test silently false on every row.
 *
 * Here rather than beside the projection because the API mints these ids and the web parses them —
 * they are a wire contract between the two, and a wire contract with a copy on each side is a wire
 * contract with a bug waiting in it.
 */
export const PLANNED_TASK_OUTLINE_LINE_ROW_PREFIX = 'planned-outline-line:';

export const PLANNED_TASK_OUTLINE_TASK_ROW_PREFIX = 'planned-outline-task:';

/**
 * The derived state only a LIVE standing task may carry, named once.
 *
 * Every field here is minted by the scheduling pipeline or by an integration caller, and every one of
 * them names something outside the row: a `scheduleId` keys due-day rows, a revision fences a
 * projection, an integration key claims a caller's idempotency slot. A second row holding any of them
 * is a second row claiming an identity that already belongs to somebody — which is why a TEMPLATE copy
 * and a VISIT exception both lose them, and why the list is data rather than a rule each of the
 * repository and the tenant upgrade re-derives for itself.
 */
export const PLANNED_TASK_LIVE_ONLY_FIELDS = ['scheduleId', 'scheduleClass', 'scheduleRevision', 'lastProjectedRevision', 'projectedUntil', 'nextDueAfterHorizon', 'lastCompleted', 'integrationId', 'integrationCallerId'] as const satisfies readonly (keyof PlannedTask)[];

/**
 * The plan-owned decisions a VISIT may not make, on top of the live-only fields above.
 *
 * The authored cadence is here rather than in `PLANNED_TASK_LIVE_ONLY_FIELDS` because a TEMPLATE
 * legitimately carries one — that is what a template is for — while a Visit exception never can: a
 * schedule on one attendance would mean that attendance recurs, which is not a thing a Visit can say.
 * `disabled` is the same shape of decision: a task the plan keeps but never schedules.
 */
export const PLANNED_TASK_STANDING_ONLY_FIELDS = [...PLANNED_TASK_LIVE_ONLY_FIELDS, 'schedule', 'scheduleExceptions', 'startsOn', 'disabled'] as const satisfies readonly (keyof PlannedTask)[];

/**
 * The CADENCE a caller may author — and therefore the only fields a subtask or template create may be
 * refused for sending.
 *
 * Deliberately NOT the two sets above, and the difference matters. Those are STRIP lists: what a row
 * on the wrong side of the collection must not be left HOLDING, whoever put it there. This is a
 * REJECT list: what a caller must not have ASKED FOR. The overlap is large enough that reusing one
 * for the other looks harmless, and it is not — `integrationId` and `integrationCallerId` are in the
 * live-only strip because a COPY must not inherit somebody's idempotency slot, but a caller creating
 * a subtask is perfectly entitled to key it with their own id. Refusing that made every public
 * subtask import fail, because the parent-scoped unique index exists precisely so two parents on one
 * line can each carry the caller's 'step-1'.
 *
 * The derived projection state is absent for the opposite reason: `scheduleId` and the revisions are
 * minted, never authored, so a caller sending one is not making a request to refuse — it is passing
 * through a field the strip will drop anyway.
 */
export const PLANNED_TASK_AUTHORED_CADENCE_FIELDS = ['schedule', 'scheduleExceptions', 'startsOn'] as const satisfies readonly (keyof PlannedTask)[];

/** What a root task's owner chain is built from: the plan, its line, and the site the plan sits at. */
export interface PlannedTaskOwnerChain {
    site?: Entity;
    servicePlan?: Entity;
    serviceLine?: Entity;
}

/**
 * The `links` a ROOT task carries, in owner order: Site, Service Plan, Service Line.
 *
 * An owner with no id contributes NOTHING rather than a placeholder — a plan with no site is stamped
 * with two links, not three with a hole in it. The due-day bitmap's no-site sentinel is a KEY concern
 * so that an unsited row still sorts; a link asserting a site named `__nosite__` would be claiming
 * something nobody has.
 *
 * Order is stable so a re-stamp of unchanged owners writes a byte-identical array, which is what keeps
 * an idempotent backfill from reporting work it did not do.
 */
export function buildPlannedTaskLinks(owners: PlannedTaskOwnerChain): EntityLink[] {

    const links: EntityLink[] = [];
    if (owners.site?._id) links.push({ _id: owners.site._id, name: owners.site.name ?? '', type: ActivityEntityName.Site });
    if (owners.servicePlan?._id) links.push({ _id: owners.servicePlan._id, name: owners.servicePlan.name ?? '', type: ActivityEntityName.ServicePlan });
    if (owners.serviceLine?._id) links.push({ _id: owners.serviceLine._id, name: owners.serviceLine.name ?? '', type: ActivityEntityName.ServiceLine });

    return links;
}
